import { useEffect, useState } from 'react'
import { api, type Desk, type Portfolio } from '../api'
import { RowMenu } from '../components/RowMenu'
import { parseDelimited } from '../csv'

/**
 * Funds — TECHNICAL_DESIGN.md §5.1, §10.1.
 *
 * Creating a portfolio is `admin` on the argument §10.1 makes about membership:
 * which funds the firm runs is an operational fact, and what binds them is the
 * approver's. Creating one binds nothing.
 *
 * Bulk import is done a row at a time against `POST /api/portfolios` rather than
 * through a bulk endpoint, so each fund is its own audit entry naming who
 * created it. A single write would record twenty funds as one act, and *who
 * added this fund* is a question asked about one of them.
 *
 * **User-facing text here says what to do, not why the design is what it is.**
 * The reasoning lives in comments like this one.
 */
interface Row {
  code: string
  name: string
  base_currency: string
  domicile?: string
  regime?: string
}

/** A pasted table, as somebody would paste it out of a spreadsheet. */
function parseRows(text: string): { rows: Row[]; problems: string[] } {
  const problems: string[] = []
  const rows: Row[] = []

  // Tab or comma, quoting and all — see `csv.ts`. A fund named
  // `Fairhaven Global Bond, Series II` used to become two columns and push the
  // currency into the name.
  const lines = parseDelimited(text)

  for (const [index, cells] of lines.entries()) {
    if (index === 0 && /^(code|fund)$/i.test(cells[0] ?? '')) continue // a header row
    const [code, name, currency, domicile, regime] = cells

    if (!code) continue
    if (!name || !currency) {
      problems.push(`${code}: needs a name and a currency`)
      continue
    }
    if (currency.length !== 3) {
      problems.push(`${code}: "${currency}" is not a three-letter currency`)
      continue
    }
    rows.push({
      code,
      name,
      base_currency: currency.toUpperCase(),
      domicile: domicile || undefined,
      regime: regime || undefined,
    })
  }
  return { rows, problems }
}

export function Funds({
  mode,
  firmWide = true,
  canManage = true,
  desk: openOn,
  onBack,
}: {
  mode: 'add' | 'list'
  /** Works across the whole firm: sees every desk's funds and says which desk each is on. */
  firmWide?: boolean
  /** Adds, corrects and moves funds and names owners (scope.create). */
  canManage?: boolean
  /** The desk to show, arriving from Desks: the list is that desk's alone. */
  desk?: string
  /** Back to Desks, where the reader came from. */
  onBack?: () => void
}) {
  const [funds, setFunds] = useState<Portfolio[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [one, setOne] = useState({ code: '', name: '', base_currency: 'USD', domicile: '', regime: '' })
  const [pasted, setPasted] = useState('')
  const [failures, setFailures] = useState<string[]>([])
  /** The accounts that may own a fund: an author or an approver, not deactivated. */
  const [people, setPeople] = useState<{ id: string; name: string; email: string }[]>([])
  /** The fund whose owner is being changed, and the account chosen. */
  const [editing, setEditing] = useState<string | null>(null)
  const [owner, setOwner] = useState('')
  /**
   * The fund being corrected, on a view of its own. Decision 51: the name, domicile and
   * regime at any time; the code and currency only while nothing refers to the fund.
   */
  const [fixing, setFixing] = useState<Portfolio | null>(null)
  const [draft, setDraft] = useState({ code: '', name: '', base_currency: '', domicile: '', regime: '' })
  /** The firm's desks. New funds go on the one chosen; with one desk there is nothing to choose. */
  const [desks, setDesks] = useState<Desk[]>([])
  const [desk, setDesk] = useState('')
  /** The fund being moved to another desk, and the desk chosen. */
  const [moving, setMoving] = useState<string | null>(null)
  const [target, setTarget] = useState('')

  /** Which desk's funds the list shows; empty is every desk the reader can see. */
  const [shown, setShown] = useState(openOn ?? '')
  /** Opened on one desk from Desks: that desk only, and no filter to change it. */
  const fixedDesk = openOn ? desks.find((d) => d.id === openOn) : undefined

  async function load() {
    setError(null)
    try {
      setFunds(await api.portfolios())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoaded(true)
    }
  }

  useEffect(() => {
    void load()
    api
      .desks()
      .then((found) => {
        setDesks(found)
        // One desk to choose from is a choice already made.
        if (found.length === 1) setDesk(found[0]!.id)
      })
      .catch(() => setDesks([]))
  }, [])

  /** Somebody across the firm always sees which desk a fund is on; a desk sees it when it has several. */
  const severalDesks = firmWide || desks.length > 1
  const deskId = severalDesks ? desk || null : null
  const listed = shown ? funds.filter((f) => f.desk_id === shown) : funds

  /** The people who may own a fund: authors and approvers who can see its desk. */
  function offerOwners(fund: Portfolio) {
    setPeople([])
    if (fund.desk_id)
      api
        .deskOwners(fund.desk_id)
        .then(setPeople)
        .catch(() => setPeople([]))
  }

  async function act(work: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setNotice(null)
    setFailures([])
    try {
      await work()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const { rows, problems } = parseRows(pasted)

  if (!loaded) return <p className="loading">Loading…</p>

  return (
    <section>
      <header className="page-head">
        <h2>Funds</h2>
        <p className="note">
          The funds this system holds guidelines for. A fund has to exist before
          any limit can be recorded against it, and before a guideline workbook
          can be matched to its columns. Adding one applies no limits — it is an
          empty record until somebody approves a rule for it.
        </p>
      </header>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}
      {failures.length > 0 && (
        <div className="warning">
          <h3>{failures.length} could not be added</h3>
          <ul className="hits">
            {failures.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {mode === 'add' && (<>
      {!severalDesks && desks.length === 1 && (
        <p className="note" data-testid="lands-on">
          Funds you add go on {desks[0]!.code}, {desks[0]!.name}.
        </p>
      )}
      {severalDesks && (
        <div className="card">
          <label>
            Desk
            <select
              value={desk}
              required
              title="The team that runs the funds you add below."
              onChange={(e) => setDesk(e.target.value)}
            >
              <option value="">Choose a desk…</option>
              {desks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} · {d.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <div className="card">
        <h3>Add a fund</h3>
        <form
          className="row-form"
          onSubmit={(e) => {
            e.preventDefault()
            void act(async () => {
              await api.createPortfolio({
                code: one.code,
                name: one.name,
                base_currency: one.base_currency,
                domicile: one.domicile || null,
                regime: one.regime || null,
                desk_id: deskId,
              })
              setNotice(`${one.code} added.`)
              setOne({ code: '', name: '', base_currency: 'USD', domicile: '', regime: '' })
            })
          }}
        >
          <label>
            Code
            <input
              value={one.code}
              required
              title="How this fund is identified in workbooks, exports and client mail. Use the code your firm already uses."
              onChange={(e) => setOne({ ...one, code: e.target.value })}
            />
          </label>
          <label>
            Name
            <input
              value={one.name}
              required
              title="The fund's full name, as people refer to it."
              onChange={(e) => setOne({ ...one, name: e.target.value })}
            />
          </label>
          <label>
            Currency
            <input
              value={one.base_currency}
              maxLength={3}
              required
              title="Three letters. The currency this fund's limits are measured in — USD, EUR, GBP."
              onChange={(e) => setOne({ ...one, base_currency: e.target.value.toUpperCase() })}
            />
          </label>
          <label>
            Domicile
            <input
              value={one.domicile}
              placeholder="optional"
              title="Where the fund is established — Ireland, Luxembourg, Delaware. Optional."
              onChange={(e) => setOne({ ...one, domicile: e.target.value })}
            />
          </label>
          <label>
            Regime
            <input
              value={one.regime}
              placeholder="optional — UCITS, 40 Act…"
              title="The rulebook the fund is run under — UCITS, 40 Act, AIF. Optional."
              onChange={(e) => setOne({ ...one, regime: e.target.value })}
            />
          </label>
          <button type="submit" disabled={busy}>
            Add
          </button>
        </form>
      </div>

      <div className="card">
        <h3>Add several at once</h3>
        <p className="note">
          Paste rows from a spreadsheet, or a comma-separated list, in this order:
          <code> code, name, currency, domicile, regime</code>. The last two are
          optional. A first row headed <code>code</code> is ignored, so you can
          paste a table with its headings.
        </p>
        <label>
          Rows
          <textarea
            rows={6}
            value={pasted}
            title="One fund per line: code, name, currency, and optionally domicile and regime. Tabs or commas both work, so a paste from a spreadsheet is fine."
            placeholder={'EMDVAL, EM Debt Value, USD\nTARN, Tarn Credit, EUR, Ireland, UCITS'}
            onChange={(e) => setPasted(e.target.value)}
          />
        </label>

        {problems.length > 0 && (
          <div className="warning">
            <h3>{problems.length} row(s) cannot be read</h3>
            <ul className="hits">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        )}

        {rows.length > 0 && (
          <>
            <p className="note">
              {rows.length} fund(s) ready to add. Check them before you do — a
              mistyped code becomes a fund nobody meant to create, and its limits
              will go on the wrong record.
            </p>
            <table className="grid">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Currency</th>
                  <th>Domicile</th>
                  <th>Regime</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.code}>
                    <td>
                      <code>{row.code}</code>
                    </td>
                    <td>{row.name}</td>
                    <td>{row.base_currency}</td>
                    <td className="clause">{row.domicile ?? ''}</td>
                    <td className="clause">{row.regime ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="actions">
              <button
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    const failed: string[] = []
                    let added = 0
                    for (const row of rows) {
                      try {
                        await api.createPortfolio({ ...row, desk_id: deskId })
                        added += 1
                      } catch (e) {
                        failed.push(`${row.code}: ${e instanceof Error ? e.message : String(e)}`)
                      }
                    }
                    // Reported per row rather than as one outcome: a run that
                    // added eighteen of twenty is not a failure, and the two
                    // that did not are what somebody has to act on.
                    setFailures(failed)
                    setNotice(`${added} fund(s) added.`)
                    if (failed.length === 0) setPasted('')
                  })
                }
              >
                Add {rows.length} fund(s)
              </button>
            </div>
          </>
        )}
      </div>

      </>)}

      {mode === 'list' && fixing && (
        <div className="card" data-testid="fund-edit">
          <h3>Edit {fixing.code}</h3>
          <form
            className="row-form"
            onSubmit={(e) => {
              e.preventDefault()
              void act(async () => {
                const saved = await api.updatePortfolio(fixing.id, {
                  code: draft.code,
                  name: draft.name,
                  base_currency: draft.base_currency,
                  domicile: draft.domicile || null,
                  regime: draft.regime || null,
                })
                setFixing(null)
                setNotice(`${saved.code} saved.`)
              })
            }}
          >
            <label>
              Code
              <input
                value={draft.code}
                required
                disabled={fixing.in_use}
                title={
                  fixing.in_use
                    ? 'Limits, holdings and exports know this fund by its code, so it stays as recorded.'
                    : 'How this fund is identified in workbooks, exports and client mail.'
                }
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
              />
            </label>
            <label>
              Name
              <input
                value={draft.name}
                required
                title="The fund's full name, as people refer to it."
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label>
              Currency
              <input
                value={draft.base_currency}
                maxLength={3}
                required
                disabled={fixing.in_use}
                title={
                  fixing.in_use
                    ? 'This fund’s limits and verdicts are measured in this currency, so it stays as recorded.'
                    : "Three letters. The currency this fund's limits are measured in."
                }
                onChange={(e) => setDraft({ ...draft, base_currency: e.target.value.toUpperCase() })}
              />
            </label>
            <label>
              Domicile
              <input
                value={draft.domicile}
                placeholder="optional"
                onChange={(e) => setDraft({ ...draft, domicile: e.target.value })}
              />
            </label>
            <label>
              Regime
              <input
                value={draft.regime}
                placeholder="optional — UCITS, 40 Act…"
                onChange={(e) => setDraft({ ...draft, regime: e.target.value })}
              />
            </label>
            <button type="submit" disabled={busy}>
              Save
            </button>
            <button type="button" className="ghost" onClick={() => setFixing(null)}>
              Cancel
            </button>
          </form>
          {fixing.in_use && (
            <p className="note">
              The code and currency stay as recorded, so every limit, holdings file and export keeps naming this fund
              the same way.
            </p>
          )}
        </div>
      )}

      {mode === 'list' && !fixing && (<>
      {openOn && (
        <p className="back" data-testid="desk-view">
          <button type="button" className="link-button" onClick={onBack}>
            ← Desks
          </button>{' '}
          Funds on <strong>{fixedDesk ? `${fixedDesk.code} · ${fixedDesk.name}` : 'this desk'}</strong>
        </p>
      )}
      {!openOn && severalDesks && desks.length > 1 && (
        <label>
          Desk
          <select aria-label="Show funds of" value={shown} onChange={(e) => setShown(e.target.value)}>
            <option value="">Every desk</option>
            {desks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} · {d.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <h3>{listed.length} fund(s)</h3>
      {funds.length === 0 ? (
        <p className="empty">None yet.</p>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Currency</th>
              <th>Domicile</th>
              <th>Regime</th>
              {severalDesks && <th>Desk</th>}
              <th>Limits of its own</th>
              <th>Owner</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {listed.map((fund) => (
              <tr key={fund.id} data-testid="fund-row">
                <td>
                  <code>{fund.code}</code>
                </td>
                <td>{fund.name}</td>
                <td>{fund.base_currency}</td>
                <td className="clause">{fund.domicile ?? ''}</td>
                <td className="clause">{fund.regime ?? ''}</td>
                {severalDesks && (
                  <td>
                    {moving === fund.id ? (
                      <span className="owner-edit">
                        <select
                          aria-label={`Desk of ${fund.code}`}
                          value={target}
                          onChange={(e) => setTarget(e.target.value)}
                        >
                          {desks.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.code}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            void act(async () => {
                              const saved = await api.moveFund(fund.id, target)
                              setMoving(null)
                              setNotice(`${fund.code} is on ${saved.desk_code}.`)
                            })
                          }
                        >
                          Save
                        </button>
                        <button type="button" className="ghost" onClick={() => setMoving(null)}>
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span title={fund.desk_name}>{fund.desk_code}</span>
                    )}
                  </td>
                )}
                <td className="limit">{fund.rule_count}</td>
                <td>
                  {editing === fund.id ? (
                    <span className="owner-edit">
                      <select
                        aria-label={`Owner of ${fund.code}`}
                        value={owner}
                        onChange={(e) => setOwner(e.target.value)}
                      >
                        <option value="">Not assigned</option>
                        {people.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} · {p.email}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void act(async () => {
                            const saved = await api.setFundOwner(fund.id, owner || null)
                            setEditing(null)
                            setNotice(
                              saved.owner
                                ? `${fund.code} is owned by ${saved.owner_name ?? saved.owner}.`
                                : `${fund.code} has no owner.`,
                            )
                          })
                        }
                      >
                        Save
                      </button>
                      <button type="button" className="ghost" onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                    </span>
                  ) : fund.owner ? (
                    <span title={fund.owner_name ? fund.owner : undefined} data-testid="fund-owner">
                      {fund.owner_name ?? fund.owner}
                    </span>
                  ) : (
                    <span className="clause">Not assigned</span>
                  )}
                </td>
                <td className="run-actions">
                  {canManage && (
                  <RowMenu
                    label={`Actions for ${fund.code}`}
                    items={[
                      {
                        label: 'Edit',
                        onSelect: () => {
                          setFixing(fund)
                          setDraft({
                            code: fund.code,
                            name: fund.name,
                            base_currency: fund.base_currency,
                            domicile: fund.domicile ?? '',
                            regime: fund.regime ?? '',
                          })
                          setNotice(null)
                          setError(null)
                        },
                      },
                      ...(severalDesks
                        ? [
                            {
                              label: 'Move to another desk',
                              onSelect: () => {
                                setMoving(fund.id)
                                setTarget(fund.desk_id ?? '')
                              },
                            },
                          ]
                        : []),
                      {
                        label: 'Change owner',
                        onSelect: () => {
                          setEditing(fund.id)
                          setOwner(fund.owner_user_id ?? '')
                          offerOwners(fund)
                        },
                      },
                      {
                        label: 'Delete',
                        danger: true,
                        disabled: fund.in_use,
                        why: fund.in_use
                          ? 'Limits, holdings or sign-offs are recorded against this fund, so it stays on record.'
                          : undefined,
                        onSelect: () => {
                          if (
                            !window.confirm(
                              `Delete ${fund.code}? Nothing is recorded against it, so nothing changes for anyone.`,
                            )
                          )
                            return
                          void act(async () => {
                            await api.deletePortfolio(fund.id)
                            setNotice(`${fund.code} deleted.`)
                          })
                        },
                      },
                    ]}
                  />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="note">
        Limits of its own counts limits written against the fund itself. A fund
        showing zero may still be covered by limits that apply to a group it
        belongs to — the Lookup screen shows both together. The owner signs the
        fund off and answers for its cure obligations; a fund with no owner takes
        the author of its first limit put into force.
      </p>
      </>)}
    </section>
  )
}
